import { Button, Modal } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./file-sharing.scss";
import { computePath, formatBytes, isValidFolderPath, SAFE_FILENAME_PATTERN } from "./fileTreeUtils";
import FolderPathSelector from "./FolderPathSelector";
import UploadStatusIcon, { FileUploadStatus } from "./UploadStatusIcon";
import { useAbortControllers } from "./useAbortControllers";
import SelectingView from "components/import-file-dialog/SelectingView";
import { FileShareQuotaResponseDto, hasHttpStatus } from "domain/fileSharing";
import {
    fileSharingService,
    generateChecksumSha256,
    MAX_TOTAL_UPLOAD_SIZE_BYTES,
} from "services/file-sharing/FileSharingService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { logger } from "utils/logging";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onUploadComplete: () => void;
    existingPaths: string[];
    quota?: FileShareQuotaResponseDto;
}

// Delay after S3 upload to allow the backend event pipeline (S3 → Lambda → DB) to complete.
const POST_UPLOAD_REFRESH_DELAY_MS = 1_500;
type UploadState = "SELECTING" | "UPLOADING" | "ERROR";

interface FileError {
    filename: string;
    message: string;
}

interface FileUploadProgress {
    filename: string;
    status: FileUploadStatus;
    errorMessage?: string;
}

const STATUS_CLASS: Record<FileUploadStatus, string> = {
    QUEUED: style.uploadStatusQueued,
    UPLOADING: style.uploadStatusUploading,
    DONE: style.uploadStatusDone,
    FAILED: style.uploadStatusFailed,
};

const STATUS_LABEL_KEY: Record<FileUploadStatus, string> = {
    QUEUED: "FileSharing.upload.statusQueued",
    UPLOADING: "FileSharing.upload.statusUploading",
    DONE: "FileSharing.upload.statusDone",
    FAILED: "FileSharing.upload.statusFailed",
};

const FileUploadModal = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const abortControllers = useAbortControllers();

    const [uploadState, setUploadState] = React.useState<UploadState>("SELECTING");
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
    const [fileErrors, setFileErrors] = React.useState<FileError[]>([]);
    const [fileProgresses, setFileProgresses] = React.useState<FileUploadProgress[]>([]);
    const [selectedPath, setSelectedPath] = React.useState<string>("");
    const [newFolder, setNewFolder] = React.useState<string>("");

    React.useEffect(() => {
        if (props.isOpen) {
            setUploadState("SELECTING");
            setErrorMessage(undefined);
            setFileErrors([]);
            setFileProgresses([]);
            setSelectedPath("");
            setNewFolder("");
        }
        return () => abortControllers.abortAll();
    }, [props.isOpen, abortControllers]);

    React.useEffect(() => {
        if (!props.isOpen) {
            return;
        }
        const preventDefault = (e: DragEvent) => e.preventDefault();
        document.addEventListener("dragover", preventDefault);
        document.addEventListener("drop", preventDefault);
        return () => {
            document.removeEventListener("dragover", preventDefault);
            document.removeEventListener("drop", preventDefault);
        };
    }, [props.isOpen]);

    const updateFileProgress = (index: number, update: Partial<Omit<FileUploadProgress, "filename">>) => {
        setFileProgresses((prev) => prev.map((fp, i) => (i === index ? { ...fp, ...update } : fp)));
    };

    const handleFiles = async (files: File[]) => {
        if (!isValidFolderPath(newFolder)) {
            setFileErrors([{ filename: "", message: t("FileSharing.upload.errorInvalidFolderName") }]);
            return;
        }

        const totalSizeBytes = files.reduce((sum, file) => sum + file.size, 0);
        const maxUploadBytes =
            props.quota?.quota !== undefined
                ? Math.max(0, props.quota.quota.diskQuotaBytes - props.quota.consumption.usedDiskBytes)
                : MAX_TOTAL_UPLOAD_SIZE_BYTES;
        if (totalSizeBytes > maxUploadBytes) {
            setFileErrors([
                {
                    filename: "",
                    message: t("FileSharing.upload.errorTooLarge", { limit: formatBytes(maxUploadBytes) }),
                },
            ]);
            return;
        }

        const initialProgresses = files.map<FileUploadProgress>((f) =>
            SAFE_FILENAME_PATTERN.test(f.name)
                ? { filename: f.name, status: "QUEUED" }
                : { filename: f.name, status: "FAILED", errorMessage: t("FileSharing.upload.errorInvalidName") }
        );

        setFileProgresses(initialProgresses);
        setUploadState("UPLOADING");
        usageStatisticsService.sendEvent({ category: Category.FILE_SHARING, action: Action.UPLOAD_FILE });
        setErrorMessage(undefined);
        setFileErrors([]);

        const validFiles = files.flatMap((f, i) => (SAFE_FILENAME_PATTERN.test(f.name) ? [{ file: f, index: i }] : []));
        if (validFiles.length === 0) {
            setUploadState("ERROR");
            return;
        }

        const uploadPath = computePath(selectedPath, newFolder) || undefined;
        let hasErrors = initialProgresses.some((p) => p.status === "FAILED");

        for (const { file, index } of validFiles) {
            const abortController = new AbortController();
            abortControllers.push(abortController);
            updateFileProgress(index, { status: "UPLOADING" });
            try {
                const buffer = await file.arrayBuffer();
                const checksum = await generateChecksumSha256(buffer);
                const filename = uploadPath ? `${uploadPath}/${file.name}` : file.name;
                const { presigned_url } = await fileSharingService.initiateUpload(
                    { filename, file_size: file.size, checksum },
                    abortController
                );
                await fileSharingService.uploadToS3(presigned_url, buffer, abortController.signal);
                updateFileProgress(index, { status: "DONE" });
            } catch (error) {
                if (abortController.signal.aborted) {
                    return;
                }
                logger.error("File upload failed", error instanceof Error ? error : new Error(String(error)));
                // The backend returns 400 when the upload would exceed the tenant's storage quota.
                const message =
                    hasHttpStatus(error) && error.status === 400
                        ? t("FileSharing.upload.errorQuotaExceeded")
                        : t("FileSharing.upload.errorGeneric");
                updateFileProgress(index, { status: "FAILED", errorMessage: message });
                hasErrors = true;
            }
        }

        if (hasErrors) {
            setUploadState("ERROR");
        } else {
            await new Promise((resolve) => setTimeout(resolve, POST_UPLOAD_REFRESH_DELAY_MS));
            props.onUploadComplete();
            props.onClose();
        }
    };

    const renderProgressList = () => {
        return (
            <div className={style.uploadFileList}>
                {fileProgresses.map((fp, i) => (
                    <div key={i} className={style.uploadFileRow}>
                        <div className={style.uploadFileRowMain}>
                            <span className={style.uploadFileName}>{fp.filename}</span>
                            <span className={STATUS_CLASS[fp.status]}>
                                <UploadStatusIcon status={fp.status} />
                                {t(STATUS_LABEL_KEY[fp.status])}
                            </span>
                        </div>
                        {fp.errorMessage && <div className={style.uploadFileError}>{fp.errorMessage}</div>}
                    </div>
                ))}
            </div>
        );
    };

    const renderContent = () => {
        if (uploadState === "UPLOADING") {
            return renderProgressList();
        }

        return (
            <>
                {fileProgresses.length > 0 && renderProgressList()}
                <div className={style.locationSection}>
                    <FolderPathSelector
                        existingPaths={props.existingPaths}
                        selectedPath={selectedPath}
                        newFolder={newFolder}
                        pathLabel={t("FileSharing.upload.pathLabel")}
                        newFolderLabel={t("FileSharing.upload.newFolderLabel")}
                        newFolderPlaceholder={t("FileSharing.upload.newFolderPlaceholder")}
                        pathSelectId="upload-path-select"
                        newFolderInputId="upload-new-folder"
                        onSelectedPathChange={setSelectedPath}
                        onNewFolderChange={setNewFolder}
                    />
                </div>
                <SelectingView
                    multiple
                    handleFileDrop={(fileList) => {
                        if (fileList?.length) {
                            void handleFiles(Array.from(fileList));
                        }
                    }}
                    onFileInputChange={(e) => {
                        if (e.target.files?.length) {
                            void handleFiles(Array.from(e.target.files));
                        }
                    }}
                    onSelectFileClicked={() => fileInputRef.current?.click()}
                    fileInputRef={fileInputRef}
                />
                {errorMessage && <div className={style.errorMessage}>{errorMessage}</div>}
                {fileErrors.map((e) => (
                    <div key={e.filename} className={style.errorMessage}>
                        {e.filename ? `${e.filename}: ` : ""}
                        {e.message}
                    </div>
                ))}
            </>
        );
    };

    return (
        <Modal
            isOpen={props.isOpen}
            hideModal={props.onClose}
            title={t("FileSharing.upload.title")}
            className={style.dialog}
            buttons={
                <Button variant="Secondary" onClick={props.onClose}>
                    {t("Common.cancel")}
                </Button>
            }
        >
            {renderContent()}
        </Modal>
    );
};

export default FileUploadModal;
